import type { TFunction } from 'react-i18next';
import type { ChatItem } from 'ts-components/PiChat/reducer';
import {
  approvalView,
  type ApprovalCardRecord,
  type ApprovalOperationView,
  type ApprovalPlanView,
  type ApprovalView,
  type SettledOutcome,
} from './approvalController';
import { approvalActionLabel, approvalCopy } from './approvalCopy';

type ToolItem = Extract<ChatItem, { kind: 'tool' }>;

/** kizen kinds are identifiers (`custom_object`); show them as words. */
const kindWords = (kind: string) => kind.replace(/[_-]+/g, ' ').trim();

/**
 * English plural of a kizen kind. Kinds are kizen identifiers rather than
 * translated copy, so they're pluralized here instead of through `t()`.
 */
export const pluralKind = (kind: string, count: number): string => {
  const words = kindWords(kind);
  if (count === 1 || !words) return words;
  if (/[^aeiou]y$/i.test(words)) return `${words.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(words)) return `${words}es`;
  return `${words}s`;
};

/** "1 field", "5 fields". */
export const countOf = (count: number, kind: string): string =>
  `${count} ${pluralKind(kind, count)}`;

/**
 * How a summary names each kizen 0.5.0 kind: a noun, and a verb when the
 * kind's meaning is not its plan action (an archive is an `update`). The
 * kicode server keeps the same table (`KIND_WORDS`, kicode
 * src/packs/kizen-admin/labels.ts) for the apply row, so the card and the row
 * read alike. `counted` kinds act on a set of records in one operation; the
 * server labels each with its count ("2 records"). A summary never shows a
 * kind, an api key or an id: those are only in the expanded detail.
 */
interface KindWords {
  noun: string;
  verb?: 'archive' | 'unarchive' | 'move' | 'import';
  counted?: true;
}

export const KIND_WORDS: Readonly<Record<string, KindWords>> = {
  object: { noun: 'object' },
  category: { noun: 'category' },
  field: { noun: 'field' },
  field_option: { noun: 'field option' },
  automation: { noun: 'automation' },
  automation_message: { noun: 'automation message' },
  email_template: { noun: 'email template' },
  automation_folder: { noun: 'automation folder' },
  dashboard: { noun: 'dashboard' },
  dashlet: { noun: 'dashlet' },
  mcp_server: { noun: 'MCP server' },
  layout: { noun: 'layout' },
  record: { noun: 'record' },
  activity: { noun: 'activity' },
  activity_field: { noun: 'activity field' },
  activity_field_option: { noun: 'activity field option' },
  stage: { noun: 'stage' },
  record_move: { noun: 'record', verb: 'move' },
  record_unarchive: { noun: 'record', verb: 'unarchive' },
  record_archive: { noun: 'record', verb: 'archive', counted: true },
  form: { noun: 'form' },
  form_field: { noun: 'form field' },
  form_field_option: { noun: 'form field option' },
  survey: { noun: 'survey' },
  survey_field: { noun: 'survey field' },
  survey_field_option: { noun: 'survey field option' },
  role: { noun: 'role' },
  team_member: { noun: 'team member' },
  permission_group: { noun: 'permission group' },
  service_account: { noun: 'service account' },
  permission_setting: { noun: 'permission setting' },
  filter_group: { noun: 'filter group' },
  quick_filter: { noun: 'quick filter' },
  column_template: { noun: 'column template' },
  record_bulk_field_value: { noun: 'record', counted: true },
  record_import: { noun: 'record', verb: 'import', counted: true },
};

const ITEM = 'item';

const kindWordsOf = (kind: string): KindWords | undefined =>
  Object.hasOwn(KIND_WORDS, kind) ? KIND_WORDS[kind] : undefined;

/** The action an operation's verb shows (untranslated); undefined when it reads "Change". */
const shownAction = (
  t: TFunction,
  op: Pick<ApprovalOperationView, 'action' | 'kind'>
): string | undefined => {
  const words = kindWordsOf(op.kind);
  if (!words || approvalActionLabel(t, op.action) === undefined) return;
  // The kind's own verb names the change it makes; a skip stays a skip.
  return words.verb && op.action !== 'skip' ? words.verb : op.action;
};

/**
 * The verb and noun an operation is described by; an unknown kind or action
 * reads "Change item" (still flagged destructive by the server).
 */
export const operationWords = (
  t: TFunction,
  op: Pick<ApprovalOperationView, 'action' | 'kind'>
): { verb: string; noun: string; counted: boolean } => {
  const words = kindWordsOf(op.kind);
  const shown = shownAction(t, op);
  const verb = shown === undefined ? undefined : approvalActionLabel(t, shown);
  if (!words || verb === undefined)
    return { verb: approvalCopy(t).change, noun: ITEM, counted: false };
  return { verb, noun: words.noun, counted: words.counted === true };
};

/** The record count a `counted` operation's label leads with ("2 records"). */
const labelCount = (label: string | undefined): number | undefined => {
  const match = label === undefined ? null : /^(\d+) /.exec(label);
  return match ? Number(match[1]) : undefined;
};

export interface OperationGroup {
  /** Stable key: action and kind. */
  id: string;
  action: string;
  kind: string;
  operations: ApprovalOperationView[];
}

/** Operations grouped by action + kind, in order of first appearance. */
export const groupOperations = (
  operations: ApprovalOperationView[]
): OperationGroup[] => {
  const groups = new Map<string, OperationGroup>();
  operations.forEach((op) => {
    const id = `${op.action}\u0000${op.kind}`;
    const group = groups.get(id);
    if (group) group.operations.push(op);
    else
      groups.set(id, {
        id,
        action: op.action,
        kind: op.kind,
        operations: [op],
      });
  });
  return [...groups.values()];
};

/** A run of summary text; `code` marks an api name, shown as an identifier. */
export interface SummarySegment {
  text: string;
  code?: true;
  /** A destructive change's verb: shown in the error colour, on the same line. */
  danger?: true;
}

/** The plain string for contexts that can't style an identifier (headers, titles). */
export const segmentsText = (segments: readonly SummarySegment[]): string =>
  segments.map((segment) => segment.text).join('');

/** The value every operation in the group shares for `field`, if they all have the same one. */
const shared = (
  operations: Pick<ApprovalOperationView, 'parentLabel' | 'parentKey'>[],
  field: 'parentLabel' | 'parentKey'
): string | undefined => {
  const value = operations[0]?.[field];
  return value !== undefined && operations.every((op) => op[field] === value)
    ? value
    : undefined;
};

/** Marks (private-use chars) where a run goes in the translated sentence, so it can be split out. */
const slot = (name: string) => `\uE000${name}\uE000`;
const SLOT = /(\uE000\w+\uE000)/;

/**
 * The translated `sentence` as runs: each slot replaced by its runs (an api
 * name stays an identifier segment), adjacent plain text merged.
 */
const fillSlots = (
  sentence: string,
  slots: Readonly<Record<string, SummarySegment[]>>
): SummarySegment[] =>
  sentence
    .split(SLOT)
    .flatMap((piece) =>
      Object.hasOwn(slots, piece) ? slots[piece] : [{ text: piece }]
    )
    .reduce<SummarySegment[]>((runs, run) => {
      const last = runs[runs.length - 1];
      if (!run.text) return runs;
      if (last && !last.code && !last.danger && !run.code && !run.danger) {
        return [...runs.slice(0, -1), { text: last.text + run.text }];
      }
      return [...runs, run];
    }, []);

/**
 * "<action> <target> on <parent>": the parent is its label when every
 * operation shares one, else its shared api name as an identifier segment
 * ("on `fermentation_tanks`"); with neither, "on …" is left out.
 */
const withParent = (
  t: TFunction,
  action: SummarySegment,
  target: SummarySegment[],
  operations: Pick<ApprovalOperationView, 'parentLabel' | 'parentKey'>[]
): SummarySegment[] => {
  const parentLabel = shared(operations, 'parentLabel');
  const parentKey = shared(operations, 'parentKey');
  const parent =
    parentLabel !== undefined
      ? [{ text: parentLabel }]
      : parentKey !== undefined
        ? [{ text: parentKey, code: true as const }]
        : undefined;
  const slots = {
    [slot('action')]: [action],
    [slot('target')]: target,
    ...(parent ? { [slot('parent')]: parent } : {}),
  };
  return fillSlots(
    parent
      ? t('{{action}} {{target}} on {{parent}}', {
          action: slot('action'),
          target: slot('target'),
          parent: slot('parent'),
        })
      : t('{{action}} {{target}}', {
          action: slot('action'),
          target: slot('target'),
        }),
    slots
  );
};

/**
 * "Create 5 fields on Fermentation Tanks", "Create field Status on
 * Fermentation Tanks" for a single named operation, "Delete field `source`
 * on Insults" for one known only by its own api name, "Archive 2 records on
 * Insults" for a counted kind. Never the kind, an api key path or an id.
 */
export const groupLabelSegments = (
  t: TFunction,
  group: OperationGroup
): SummarySegment[] => {
  const { verb: action, noun, counted } = operationWords(t, group);
  const [only] = group.operations;
  let target: SummarySegment[];
  if (counted) {
    const counts = group.operations.map((op) => labelCount(op.label));
    target = [
      {
        text: counts.every((n): n is number => n !== undefined)
          ? countOf(
              counts.reduce((a, b) => a + b, 0),
              noun
            )
          : pluralKind(noun, 2),
      },
    ];
  } else if (group.operations.length === 1 && only.label !== undefined) {
    target = [{ text: `${noun} ${only.label}` }];
  } else if (group.operations.length === 1 && only.apiName !== undefined) {
    target = [{ text: `${noun} ` }, { text: only.apiName, code: true }];
  } else {
    target = [{ text: countOf(group.operations.length, noun) }];
  }
  // The verb carries the warning: red when any of the group's changes is destructive.
  const danger = group.operations.some((op) => op.emphasized);
  return withParent(
    t,
    danger ? { text: action, danger } : { text: action },
    target,
    group.operations
  );
};

const MAX_SUMMARY_GROUPS = 2;

/**
 * One readable line per plan, built from its operations ("Create 5 fields on
 * Papers"), the same sentence as the server's apply row. More than two kinds
 * of change, or a truncated list, read "Change 7 items" (with the parent only
 * when every operation is listed). kizen's own summary is never shown: it
 * carries api names and ids.
 */
export const planSummarySegments = (
  t: TFunction,
  plan: ApprovalPlanView
): SummarySegment[] => {
  const groups = groupOperations(plan.operations);
  if (
    plan.moreCount === 0 &&
    groups.length > 0 &&
    groups.length <= MAX_SUMMARY_GROUPS
  ) {
    return groups.flatMap((group, index) => [
      ...(index > 0 ? [{ text: ' · ' }] : []),
      ...groupLabelSegments(t, group),
    ]);
  }
  return withParent(
    t,
    plan.operations.some((op) => op.emphasized)
      ? { text: approvalCopy(t).change, danger: true }
      : { text: approvalCopy(t).change },
    [{ text: countOf(plan.counts.total, ITEM) }],
    plan.moreCount === 0 ? plan.operations : []
  );
};

/** The summary as plain text (the api-name segment unstyled). */
export const planSummary = (t: TFunction, plan: ApprovalPlanView): string =>
  segmentsText(planSummarySegments(t, plan));

/**
 * What a destructive operation removes. kizen sends only action and kind, so
 * the effect comes from the action; null for operations that aren't destructive.
 */
export const destructiveEffect = (
  t: TFunction,
  op: Pick<ApprovalOperationView, 'action' | 'emphasized' | 'effect'>
): string | null => {
  if (!op.emphasized) return null;
  switch (op.effect) {
    case 'deletes':
      return t('Deletes');
    case 'archives':
      return t('Archives records');
    case 'removes_roles':
      return t('Removes roles');
    case 'replaces_options':
      return t('Replaces options');
    case 'removes_values':
      return t('Removes values');
    case 'service_account':
      return t('Changes a service account');
    case 'unrecognized':
      return t('Unrecognized change');
    default:
      break;
  }
  // No effect from the server: say what the action implies.
  switch (op.action) {
    case 'delete':
      return t('Deletes');
    case 'update':
    case 'upsert':
    case 'apply':
      return t('Replaces existing');
    default:
      return t('Destructive');
  }
};

/**
 * The short inline note a destructive change gets when its red verb alone
 * doesn't say what it removes ("Update field Status — replaces options");
 * null when the shown verb already says it deletes or archives, and for
 * anything not destructive.
 */
export const destructiveNote = (
  t: TFunction,
  op: Pick<ApprovalOperationView, 'action' | 'kind' | 'emphasized' | 'effect'>
): string | null => {
  if (!op.emphasized) return null;
  const shown = shownAction(t, op);
  switch (op.effect) {
    case 'deletes':
      return shown === 'delete' ? null : t('deletes');
    case 'archives':
      return shown === 'archive' ? null : t('archives records');
    case 'removes_roles':
      return t('removes roles');
    case 'replaces_options':
      return t('replaces options');
    case 'removes_values':
      return t('removes values');
    case 'service_account':
      return t('changes a service account');
    case 'unrecognized':
      return t('unrecognized change');
    default:
      break;
  }
  if (shown === 'delete' || shown === 'archive') return null;
  if (op.action === 'delete') return t('deletes');
  return op.action === 'update' ||
    op.action === 'upsert' ||
    op.action === 'apply'
    ? t('replaces existing')
    : t('destructive');
};

export interface OperationRowDisplay {
  id: string;
  /**
   * Verb and kind, e.g. "Create field", only in a mixed plan; null when every
   * operation shares them and the summary already says so.
   */
  verb: string | null;
  /** `verb` in runs, its action word marked `danger` for a destructive change; null with it. */
  verbSegments: SummarySegment[] | null;
  /** The human-readable name (`label`), else the target's own api name, else its noun. */
  name: string;
  /** `name` is the target's own api name: shown as an identifier. */
  nameIsApiName?: true;
  /** Quiet secondary detail, e.g. "Dropdown". */
  typeLabel?: string;
  /** The api key, for the expanded detail only. */
  key: string;
  /** What a destructive change removes (data model; not shown on its own line). */
  effect: string | null;
  /** The inline red note after the name when the verb alone doesn't say it ("replaces options"). */
  note: string | null;
  detail?: string;
  /** Always true: the api key is shown only when the row is expanded. */
  expandable: boolean;
}

export interface PlanDisplay {
  planId: string;
  /** The summary in runs, with an api-name parent marked as code. */
  summarySegments: SummarySegment[];
  /** Says how many operations the server left out (never destructive ones); null when none. */
  hiddenNote: string | null;
  /**
   * A one-operation plan has no rows: its summary names the operation, and
   * the operation's effect and payload hang off the summary instead.
   */
  summaryEffect: string | null;
  /** The single operation's inline red note (`destructiveNote`); null for the rest. */
  summaryNote: string | null;
  summaryDetail?: string;
  /** The single operation's api key, for its expanded detail (never in the summary). */
  summaryKey?: string;
  /** Every listed operation, ordered by action + kind group. */
  rows: OperationRowDisplay[];
}

/**
 * The quiet note under a truncated plan. Only non-destructive operations can be
 * left out: the parser refuses a plan that lists fewer destructive operations
 * than it counts.
 */
export const hiddenOperationsNote = (
  t: TFunction,
  count: number
): string | null => {
  if (count <= 0) return null;
  return count === 1
    ? t('1 more non-destructive operation not shown')
    : t('{{count}} more non-destructive operations not shown', { count });
};

/**
 * The row's verb: "Create field", or just "Archive" for a counted kind, whose
 * name is already "12 records".
 */
export const operationLabel = (
  t: TFunction,
  op: Pick<ApprovalOperationView, 'action' | 'kind'>
): string => segmentsText(operationLabelSegments(t, op, false));

/** The row's verb in runs: the action word marked `danger` for a destructive change. */
const operationLabelSegments = (
  t: TFunction,
  op: Pick<ApprovalOperationView, 'action' | 'kind'>,
  danger: boolean
): SummarySegment[] => {
  const { verb, noun, counted } = operationWords(t, op);
  const action: SummarySegment = danger
    ? { text: verb, danger: true }
    : { text: verb };
  return counted
    ? [action]
    : fillSlots(
        t('{{action}} {{kind}}', { action: slot('action'), kind: noun }),
        {
          [slot('action')]: [action],
        }
      );
};

const capitalized = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The row's name: its label, else the target's own api name (`source`, as
 * an identifier), else its noun ("Record", or "Record 2" among several).
 * Never the api key path, which is only in the expanded detail.
 */
const rowName = (
  t: TFunction,
  op: ApprovalOperationView,
  index: number,
  groupSize: number
): Pick<OperationRowDisplay, 'name' | 'nameIsApiName'> => {
  if (op.label !== undefined) return { name: op.label };
  if (op.apiName !== undefined)
    return { name: op.apiName, nameIsApiName: true };
  const noun = capitalized(operationWords(t, op).noun);
  return { name: groupSize > 1 ? `${noun} ${index + 1}` : noun };
};

export const planDisplay = (
  t: TFunction,
  plan: ApprovalPlanView
): PlanDisplay => {
  const base = {
    planId: plan.planId,
    summarySegments: planSummarySegments(t, plan),
    hiddenNote: hiddenOperationsNote(t, plan.moreCount),
  };
  const [only] = plan.operations;
  if (plan.operations.length === 1 && plan.moreCount === 0) {
    return {
      ...base,
      summaryEffect: destructiveEffect(t, only),
      summaryNote: destructiveNote(t, only),
      ...(only.detail === undefined ? {} : { summaryDetail: only.detail }),
      // The summary never names the api key; the expanded detail does.
      summaryKey: only.key,
      rows: [],
    };
  }
  const groups = groupOperations(plan.operations);
  // Rows drop their verb only when the summary already states it: one kind of
  // change and every operation listed. A truncated plan's summary is "Change N
  // items", so its rows keep their verb (red when destructive).
  const uniform = groups.length === 1 && plan.moreCount === 0;
  return {
    ...base,
    summaryEffect: null,
    summaryNote: null,
    rows: groups.flatMap((group) =>
      group.operations.map((op, index) => {
        return {
          id: op.id,
          verb: uniform ? null : operationLabel(t, op),
          verbSegments: uniform
            ? null
            : operationLabelSegments(t, op, op.emphasized),
          ...rowName(t, op, index, group.operations.length),
          ...(op.typeLabel === undefined ? {} : { typeLabel: op.typeLabel }),
          key: op.key,
          effect: destructiveEffect(t, op),
          note: destructiveNote(t, op),
          ...(op.detail === undefined ? {} : { detail: op.detail }),
          // The api key is only shown expanded.
          expandable: true,
        };
      })
    ),
  };
};

/**
 * The header of the tool row a card is anchored to while its call runs, in
 * the server's `apply: <summary>` form, so the row never shows a raw plan id.
 */
export const anchoredToolTitle = (
  t: TFunction,
  cards: readonly ApprovalCardRecord[]
): string | null => {
  const card = cards[cards.length - 1];
  if (!card) return null;
  if (card.outcome === 'pending') return t('waiting for approval');
  const view = approvalView(card);
  if (view.kind === 'command') return view.commandLine;
  return view.plans.length === 1
    ? t('apply: {{summary}}', { summary: planSummary(t, view.plans[0]) })
    : t('apply {{count}} plans', { count: view.plans.length });
};

export type ApprovalButtonId = 'approve-once' | 'approve-window' | 'decline';

export interface ApprovalAction {
  id: ApprovalButtonId;
  label: string;
}

export interface ApprovalFooter {
  /** A red text button, first. */
  decline: ApprovalAction;
  /** The primary action, last at the trailing edge. */
  approve: ApprovalAction;
  /**
   * The approve button's dropdown option, when the request allows a window;
   * null renders a plain Approve button.
   */
  approveWindow: ApprovalAction | null;
}

/** Decline · Approve, with "Approve for N minutes" in Approve's dropdown when allowed. */
export const approvalFooter = (
  t: TFunction,
  view: Pick<ApprovalView, 'showWindowOption' | 'windowMinutes'>
): ApprovalFooter => ({
  decline: { id: 'decline', label: t('Decline') },
  approve: { id: 'approve-once', label: t('Approve') },
  approveWindow: view.showWindowOption
    ? {
        id: 'approve-window',
        label:
          view.windowMinutes === 1
            ? t('Approve for 1 minute')
            : t('Approve for {{minutes}} minutes', {
                minutes: view.windowMinutes,
              }),
      }
    : null,
});

/** How many changes the card asks about: every operation of every plan, or one command. */
export const approvalChangeCount = (view: ApprovalView): number =>
  view.kind === 'command'
    ? 1
    : view.plans.reduce((n, plan) => n + plan.counts.total, 0);

/** The card's heading: "Approve 1 change", "Approve 5 changes". */
export const approvalHeading = (t: TFunction, view: ApprovalView): string => {
  const count = approvalChangeCount(view);
  return count === 1
    ? t('Approve 1 change')
    : t('Approve {{count}} changes', { count });
};

/** The compact line an answered card collapses to. */
export const outcomeLabel = (
  t: TFunction,
  card: { outcome: SettledOutcome | 'sending'; windowMinutes: number }
): string => {
  switch (card.outcome) {
    case 'sending':
      return t('Approving');
    case 'failed':
      return t('Approval not sent');
    case 'approved':
      return t('Approved');
    case 'approved_window':
      return card.windowMinutes === 1
        ? t('Approved for 1 minute')
        : t('Approved for {{minutes}} minutes', {
            minutes: card.windowMinutes,
          });
    case 'declined':
      return t('Declined');
    case 'cancelled':
      return t('Cancelled');
    case 'expired':
      return t('Expired');
  }
};

export interface ResolvedLine {
  /** The icon: approved, still uploading the grant, the grant never arrived, or anything else. */
  tone: 'approved' | 'sending' | 'failed' | 'other';
  outcome: string;
  summary: string;
}

/** The one line an answered card collapses to: icon choice, outcome, and what was asked. */
export const resolvedLine = (
  t: TFunction,
  card: ApprovalCardRecord & { outcome: SettledOutcome | 'sending' }
): ResolvedLine => ({
  tone:
    card.outcome === 'approved' || card.outcome === 'approved_window'
      ? 'approved'
      : card.outcome === 'sending' || card.outcome === 'failed'
        ? card.outcome
        : 'other',
  outcome: outcomeLabel(t, card),
  summary: requestSummary(t, approvalView(card)),
});

/** One line naming what a card asked for, for its settled state. */
export const requestSummary = (t: TFunction, view: ApprovalView): string =>
  view.kind === 'command'
    ? view.commandLine
    : view.plans.map((plan) => planSummary(t, plan)).join(' · ');

/**
 * Whether a card that just mounted should take focus. `composerHadFocus` is
 * read during the card's first render, before the commit that swaps the
 * locked composer's textarea out (by layout time focus has already fallen to
 * <body>). Focus that has since moved somewhere real is left alone.
 */
export const shouldTakeFocus = ({
  composerHadFocus,
  focusNow,
}: {
  composerHadFocus: boolean;
  /** Where focus is at layout time: fallen to <body>, still on the composer, or elsewhere. */
  focusNow: 'body' | 'composer' | 'elsewhere';
}): boolean => composerHadFocus && focusNow !== 'elsewhere';

/** True while any card waits on the user: the composer stays disabled. */
export const hasPendingApproval = (
  cards: readonly Pick<ApprovalCardRecord, 'outcome'>[]
) => cards.some((card) => card.outcome === 'pending');

/**
 * The tool row each card renders under: the row for the frame's toolCallId
 * (live rows use it as their id, history rows carry it), else the newest tool
 * row so a card is never left out of the stream.
 */
export const approvalAnchors = (
  items: readonly ChatItem[],
  cards: readonly ApprovalCardRecord[]
): Map<string, ApprovalCardRecord[]> => {
  const anchors = new Map<string, ApprovalCardRecord[]>();
  if (!cards.length) return anchors;
  const tools = items.filter((item): item is ToolItem => item.kind === 'tool');
  const last = tools[tools.length - 1];
  cards.forEach((card) => {
    const row =
      tools.find(
        (tool) =>
          tool.id === card.toolCallId || tool.toolCallId === card.toolCallId
      ) ?? last;
    if (!row) return;
    anchors.set(row.id, [...(anchors.get(row.id) ?? []), card]);
  });
  return anchors;
};

/**
 * The tool row as shown under its card: until the server sends its own
 * display, the header names what the card asks for instead of the raw args.
 */
export const anchoredToolItem = (
  t: TFunction,
  item: ToolItem,
  cards: readonly ApprovalCardRecord[] | undefined
): ToolItem => {
  // Only the call that asked: a card on the newest-row fallback says nothing about that row.
  const own = cards?.filter(
    (card) => card.toolCallId === item.id || card.toolCallId === item.toolCallId
  );
  if (item.display || !own?.length) return item;
  const title = anchoredToolTitle(t, own);
  return title ? { ...item, display: { title } } : item;
};
